import { useMemo, useState } from "react"

import { useTestSetsList } from "@/test-set/catalog"

import { useCollectionFilters } from "@/hooks/useCollectionFilters"
import { SimulatorType } from "@/types/api/SimulatorType"
import type { RunRowLike } from "@/types/RunRow"

export function useRunResultsPropertyFilters(
  rows: RunRowLike[],
  allRows: RunRowLike[],
  agentsById: Record<string, { display_name: string }> | null,
  personasById: Record<string, { display_name: string }> | null,
  isShared: boolean,
) {
  const { data: testSets } = useTestSetsList({ enabled: !isShared })
  const rowPersonaNames = useMemo(() => {
    const names = new Map<string, RunRowLike["persona_display_name"]>()
    for (const row of allRows) {
      // Keep the first row's snapshot, matching the shared-result fallback.
      if (row.persona_id && !names.has(row.persona_id)) {
        names.set(row.persona_id, row.persona_display_name)
      }
    }
    return names
  }, [allRows])
  const agentName = (id: string) => agentsById?.[id]?.display_name || id
  const personaName = (id: string) =>
    personasById?.[id]?.display_name || rowPersonaNames.get(id) || id
  const [searchQuery, setSearchQuery] = useState("")
  const filters = useCollectionFilters(rows, [
    {
      id: "agent",
      options: agentsById
        ? Object.keys(agentsById)
        : allRows.map((row) => row.agent_id),
      label: "Agent",
      kind: "select",
      value: (row) => row.agent_id,
      formatValue: agentName,
    },
    {
      id: "persona",
      options: personasById
        ? Object.keys(personasById)
        : allRows.map((row) => row.persona_id),
      label: "Persona",
      kind: "select",
      value: (row) => row.persona_id,
      formatValue: personaName,
    },
    {
      id: "test-set",
      label: "Test Set",
      kind: "select",
      options: isShared
        ? allRows.map((row) => row.test_set_id)
        : (testSets ?? []).map((testSet) => ({
            value: testSet.id,
            label: testSet.display_name || testSet.id,
          })),
      value: (row) => row.test_set_id,
    },

    {
      id: "simulator",
      options: Object.values(SimulatorType),
      label: "Simulator",
      kind: "select",
      value: (row) => row.model_type,
    },
    {
      id: "source",
      options: ["Uploaded", "Simulated"],
      label: "Source",
      kind: "select",
      value: (row) => (row.is_monitoring ? "Uploaded" : "Simulated"),
    },
    {
      id: "duration",
      label: "Audio Duration (seconds)",
      kind: "number",
      value: (row) => row.audio_file_duration_seconds,
    },
    {
      id: "created",
      label: "Created",
      kind: "date",
      value: (row) => row.created_at,
    },
    {
      id: "error",
      label: "Error Message",
      kind: "text",
      value: (row) => row.error_message,
    },
    { id: "notes", label: "Notes", kind: "text", value: (row) => row.notes },
    {
      id: "visibility",
      options: ["Public", "Private"],
      label: "Visibility",
      kind: "select",
      value: (row) => (row.is_public ? "Public" : "Private"),
    },
    {
      id: "metadata",
      label: "Metadata",
      kind: "text",
      value: (row) =>
        Object.entries(row.customer_metadata ?? {}).map(
          ([key, value]) =>
            `${key}: ${typeof value === "object" ? JSON.stringify(value) : String(value)}`,
        ),
    },
  ])
  const query = searchQuery.trim().toLowerCase()
  return {
    ...filters,
    searchQuery,
    setSearchQuery,
    rows: query
      ? filters.rows.filter((row) =>
          [
            row.simulation_output_id,
            row.run_id,
            row.test_set_row_id,
            row.test_case_description,
            row.input_str,
            row.notes,
            row.error_message,
            row.agent_id && agentName(row.agent_id),
            row.persona_id && personaName(row.persona_id),
          ].some((value) => value?.toLowerCase().includes(query)),
        )
      : filters.rows,
  }
}
